import React, { useEffect, useState, useRef, useContext } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import axios from "axios";
import { ApiBaseUrlContext } from "../App";
import { useTranslation } from "react-i18next";

const MachineQRCode = ({ machineId }) => {
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const [machineData, setMachineData] = useState(null);
  const [registrationCode, setRegistrationCode] = useState('');
  const qrContainerRef = useRef(null);
  const regCodeRef = useRef(null);
  const qrCanvasRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Fetch machine info
    axios.get(`${apiBaseUrl}/mnt/api/machines/all?includeAll=true`)
      .then(res => {
        const machine = res.data.find(m => m.machineid === machineId);
        if (machine) setMachineData(machine);
      })
      .catch(err => console.error("Error fetching machine data:", err));

    // Fetch registration code from settings
    axios.get(`${apiBaseUrl}/mnt/api/settings`)
      .then(res => {
        setRegistrationCode(res.data.registrationCode || '');
      })
      .catch(err => console.error("Error fetching registration code:", err));
  }, [machineId, apiBaseUrl]);

  // Dark mode detection
  const isDarkMode = document.body.classList.contains("theme--dark");

  // Build QR code URL
  const token = localStorage.getItem("token");
  const redirectPath = `/mnt/fault-error?machineId=${machineId}`;
  const qrCodeUrl = token
    ? `${window.location.origin}${redirectPath}`
    : `${window.location.origin}/qr-login?redirect=${encodeURIComponent(redirectPath)}&regCode=${registrationCode}`;

  /**
   * Download full QR card image (hides container styles briefly)
   */
  const downloadCardImage = async () => {
    if (!qrContainerRef.current) return;
    const el = qrContainerRef.current;
    el.style.border = "none";
    el.style.background = "transparent";

    const canvas = await html2canvas(el, { scale: 3, backgroundColor: null });

    el.style.border = "2px solid #000";
    el.style.background = "#fff";

    const imgData = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = imgData;
    link.download = `machine-${machineId}-qr-card.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Download QR card without registration code
   */
  const downloadCardWithoutCode = async () => {
    if (!qrContainerRef.current || !regCodeRef.current) return;
    const el = qrContainerRef.current;
    // Hide registration code only
    regCodeRef.current.style.visibility = 'hidden';
    // Remove container border/bg for clean capture
    el.style.border = "none";
    el.style.background = "transparent";

    const canvas = await html2canvas(el, { scale: 3, backgroundColor: null });

    // Restore registration code and styles
    regCodeRef.current.style.visibility = 'visible';
    el.style.border = "2px solid #000";
    el.style.background = "#fff";

    const imgData = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = imgData;
    link.download = `machine-${machineId}-qr-no-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!machineData) return <p>Loading...</p>;

  return (
    <div style={{ textAlign: "left", margin: "20px 0" }}>
      <div
        ref={qrContainerRef}
        style={{
          width: "190mm",
          height: "100mm",
          border: "2px solid #000",
          borderRadius: "15px",
          padding: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "#fff",
          position: "relative",
          color: isDarkMode ? "#000" : "#000"
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <QRCodeCanvas
            value={qrCodeUrl}
            size={200}
            level="H"
            ref={qrCanvasRef}
          />
          <div ref={regCodeRef} style={{ marginTop: "8px", fontSize: "14px", textAlign: "center" }}>
            Registration code: <strong>{registrationCode || 'N/A'}</strong>
          </div>
        </div>

        <div style={{ flex: 1, paddingLeft: "15px" }}>
          <h2 style={{ margin: "0", fontSize: "20px" }}>{machineData.machine}</h2>
          <p style={{ margin: "5px 0" }}><strong>Group Name:</strong> {machineData.dncmaxgroupname}</p>
          <p style={{ margin: "5px 0" }}><strong>Category:</strong> {machineData.category}</p>
          <p style={{ margin: "5px 0" }}><strong>System:</strong> {machineData.system}</p>
        </div>

        <img
          src={`${apiBaseUrl}/mnt/api/logo`}
          alt="Company Logo"
          style={{
            maxWidth: "200px",
            maxHeight: "100px",
            position: "absolute",
            top: "15px",
            right: "15px"
          }}
        />
      </div>

      {/* Buttons for downloading */}
      <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
        <button
          onClick={downloadCardImage}
          style={{ padding: "8px 12px", fontSize: "14px", cursor: "pointer" }}
        >
          {t("addMachine.downloadQRCode")}
        </button>
        <button
          onClick={downloadCardWithoutCode}
          style={{ padding: "8px 12px", fontSize: "14px", cursor: "pointer" }}
        >
          {t("addMachine.downloadWithoutCode", "Download without registration code")}
        </button>
      </div>
    </div>
  );
};

export default MachineQRCode;
